/* eslint-disable import/extensions */
/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Space, Tooltip, Select } from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FileProtectOutlined, FileSearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { Filters, FiltersCard } from "../../../components/Filters";
import { Label } from "../../../components/ui";

import api from "../../../services/api";

import "./index.css";

import { useToast } from "../../../hooks/toast";
// eslint-disable-next-line import/extensions
import Table from "../../../components/Table";
import UserAvatar from "../../../components/UserAvatar";
import { ICustomer } from "../../Customers";
import { IUser } from "../../Users";
import { useAuth } from "../../../hooks/auth";
import ProjectTypes from "../../../enums/ProjectTypes";
import { IRequirement } from "../../Project";
import StatusDateExpected from "../StatusDateExpected";
import UserRoles from "../../../enums/UserRoles";
import HistoricActivityModal from "../HistoricActivityModal";
import NameProjectTypes from "../../../enums/NameProjectTypes";
import { removeUndefinedKeys } from "../../../utils/formatters";
import ActivityStatusDate from "../../../enums/ActivityStatusDate";
import filterOptions from "../../../utils/filterOptions";
import ProjectStatus from "../../../enums/ProjectStatus";

export interface IProject {
  id: string;
  identifier: string;
  short_description: string;
  full_description: string;
  contractor_name: string;
  contractor_email: string;
  contractor_phone_number: string;
  status: string;
  purchase_order: string;
  customer: ICustomer;
  manager: IUser;
  projectType: IProjectType;
}

export interface IProjectType {
  id: string;
  name: string;
  acronym: ProjectTypes;
}

interface ILabelOption {
  label: string;
  value: string;
}

const Activities: React.FC = () => {
  const [selectedActivity, setSelectedActivity] = useState<IRequirement>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [activityFactory, setActivityFactory] = useState<any[]>([]);
  const [projectTypeId, setProjectTypeId] = useState<string>();
  const [modalVisibleSheet, setModalVisibleSheet] = useState<boolean>(false);
  const [projectsOptions, setProjectsOptions] = useState<ILabelOption[]>();
  const [usersOptions, setUsersOptions] = useState<ILabelOption[]>();

  const [showFilters, setShowFilters] = useState(false);

  const { addToast } = useToast();
  const { t } = useTranslation(["activities"]);
  const { user } = useAuth();

  // Filters
  const [factoryProjectFilter, setFactoryProjectFilter] = useState<
    string | undefined
  >(undefined);
  const [statusActivityFilter, setStatusActivityFilter] = useState<
    string | undefined
  >(undefined);
  const [userActivityFilter, setUserActivityFilter] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    api
      .get<IProjectType[]>("/project-types", {
        params: { name: NameProjectTypes.Factory },
      })
      .then(response => {
        setProjectTypeId(response.data[0].id);
      });
  }, []);

  useEffect(() => {
    api.get<IUser[]>("/users").then(response => {
      const usersOptionsData = response.data
        ?.sort((a, b) => a.name.localeCompare(b.name))
        .map(user => ({
          label: user.name,
          value: user.id,
        }));
      setUsersOptions(usersOptionsData);
    });
  }, []);

  const getProjectFactory = useCallback(async (project_type_id: string) => {
    if (project_type_id) {
      const projectsFactory = await api.get<IProject[]>("projects", {
        params: { project_type_id },
      });

      const projectsOptionsData = projectsFactory.data.map(user => ({
        label: user.short_description,
        value: user.id,
      }));

      setProjectsOptions(projectsOptionsData);
    }
  }, []);

  const statusOptions = useMemo(() => {
    return [
      {
        label: t(`status_date.${ActivityStatusDate.NoStart}`),
        value: ActivityStatusDate.NoStart,
      },
      {
        label: t(`status_date.${ActivityStatusDate.StartAndDelayed}`),
        value: ActivityStatusDate.StartAndDelayed,
      },
      {
        label: t(`status_date.${ActivityStatusDate.StartOnTime}`),
        value: ActivityStatusDate.StartOnTime,
      },
      {
        label: t(`status_date.${ActivityStatusDate.NoStartAndDelayed}`),
        value: ActivityStatusDate.NoStartAndDelayed,
      },
      {
        label: t(`status_date.${ActivityStatusDate.Executed}`),
        value: ActivityStatusDate.Executed,
      },
    ];
  }, [t]);

  useEffect(() => {
    getProjectFactory(projectTypeId || "").then();
  }, [projectTypeId]);

  const extraFilters = useMemo(
    () =>
      showFilters ? (
        <FiltersCard>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("Projetos")}</Label>
            </div>
            <Select
              placeholder={t("projetos")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              filterOption={filterOptions}
              onChange={value => setFactoryProjectFilter(value)}
              options={projectsOptions}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("status de desenvolvimento")}</Label>
            </div>
            <Select
              placeholder={t("status de desenvolvimento")}
              style={{ width: "100%" }}
              allowClear
              onChange={value => setStatusActivityFilter(value)}
              options={statusOptions}
            />
          </Col>
          <Col lg={12} md={12} sm={24} xl={8} xs={24}>
            <div>
              <Label color="#575962">{t("profissional")}</Label>
            </div>
            <Select
              placeholder={t("profissional")}
              style={{ width: "100%" }}
              allowClear
              showSearch
              filterOption={filterOptions}
              onChange={value => setUserActivityFilter(value)}
              options={usersOptions}
            />
          </Col>
        </FiltersCard>
      ) : (
        <></>
      ),
    [showFilters, t],
  );

  const factoryProjectParams = useMemo(() => {
    return {
      project_id: factoryProjectFilter,
      date_activity: statusActivityFilter,
      consultant_id: userActivityFilter,
      project_status: [
        ProjectStatus.WaitingDevelopment,
        ProjectStatus.Development,
        ProjectStatus.Approved,
      ],
    };
  }, [factoryProjectFilter, statusActivityFilter, userActivityFilter]);

  useEffect(() => {
    setLoadingAllocations(true);
    api
      .get<IRequirement[]>("/activities/factory", {
        params: removeUndefinedKeys(factoryProjectParams),
      })
      .then(async response => {
        setActivityFactory(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os seus meses de alocação, tente novamente.",
        });
      })
      .finally(() => setLoadingAllocations(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalVisibleSheet, factoryProjectParams, projectTypeId]);

  const updadeActivityHistory = useCallback((activity: IRequirement) => {
    setSelectedActivity(activity);
    setModalVisible(true);
  }, []);

  const factoryActivitiesColumns: ColumnsType<IRequirement> = [
    {
      title: t("table_column.identifier_project"),
      dataIndex: "project",
      render: (project: IProject) => {
        return <div className="antd-width-col">{project?.identifier}</div>;
      },
    },
    {
      title: t("project"),
      dataIndex: "project",
      render: (project: IProject) => {
        return (
          <div title={project?.short_description} className="antd-width-col-2">
            <Link to={`/app/projects/${project?.id}`}>
              {project?.short_description}
            </Link>
          </div>
        );
      },
    },
    {
      title: t("consultant"),
      dataIndex: "consultant",
      align: "center",
      render: (consultant: IUser) => {
        if (consultant) {
          return (
            <UserAvatar
              name={consultant.name}
              url={consultant.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("table_column.status_dev"),
      dataIndex: "date_begin_activity",
      render: (
        date_begin_activity: string,
        { expected_date_end, expected_date_begin, status }: IRequirement,
      ) => {
        return (
          <StatusDateExpected
            beginDate={expected_date_begin}
            endDate={expected_date_end}
            date_begin_activity={date_begin_activity}
            status={status}
          />
        );
      },
    },
    {
      title: t("table_column.identifier_activity"),
      dataIndex: "identifier",
      render: (identifier: string) => {
        return <div className="antd-width-col">{identifier}</div>;
      },
    },
    {
      title: t("description"),
      dataIndex: "description",
      render: (description: string) => {
        return (
          <div title={description} className="antd-width-col-3">
            {description}
          </div>
        );
      },
    },
    {
      title: t("table_column.expected_date_begin"),
      dataIndex: "expected_date_begin",
      render: (expected_date_begin: string) => {
        const beginDate = moment(expected_date_begin);
        if (!moment(beginDate).isValid()) return "";
        return (
          <div className="antd-width-col-0-9">
            {moment(expected_date_begin).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      title: t("table_column.expected_date_end"),
      dataIndex: "expected_date_end",
      render: (expected_date_end: string) => {
        const endDate = moment(expected_date_end);
        if (!moment(endDate).isValid()) return "";
        return (
          <div className="antd-width-col-0-9">
            {moment(expected_date_end).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      title: t("actions.label"),
      dataIndex: "actions",
      fixed: "right",
      render: (_, record) => (
        <>
          <Tooltip title="Exibir Descrição do desenvolvimento da Atividade">
            <Button
              onClick={() => updadeActivityHistory(record)}
              type="link"
              disabled={
                !record?.comments?.filter(c => c.comment_activity).length
              }
              icon={
                <FileProtectOutlined
                  style={{
                    color: record?.comments?.filter(c => c.comment_activity)
                      .length
                      ? "green"
                      : "red",
                  }}
                />
              }
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const handleShowFilters = useCallback(
    value => {
      setShowFilters(value);
      // As the 'showFilters' has not changed yet, within this function,
      // its logic is the reverse to clear all filters (false = true)

      if (showFilters) {
        setFactoryProjectFilter(undefined);
        setStatusActivityFilter(undefined);
        setUserActivityFilter(undefined);
      }
    },
    [showFilters],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedActivity(undefined);
    setModalVisibleSheet(false);
    setModalVisible(false);
  }, []);

  const validatedUser = useMemo(() => {
    return (
      user.role === UserRoles.Administrative || user.role === UserRoles.Manager
    );
  }, [user]);

  if (!validatedUser) {
    return <></>;
  }

  return (
    <>
      <HistoricActivityModal
        activity={selectedActivity}
        visible={modalVisible}
        handleClose={handleCloseModal}
        editHistoricActivity
      />
      <Space direction="vertical" style={{ display: "flex", marginTop: 16 }}>
        <Table
          data={activityFactory}
          columns={factoryActivitiesColumns}
          title={
            <>
              Atividades de Fabrica
              <Filters
                showFilters={showFilters}
                setShowFilters={handleShowFilters}
              />
            </>
          }
          footer={() => <></>}
          loading={loadingAllocations}
          pagination
          extraFilters={extraFilters}
        />
      </Space>
    </>
  );
};

export default Activities;
